<template>
  <div>
    <div
      class="sn-file-input"
      :style="boxStyles"
    >
      <v-row
        v-if="files.length === 0 || multiple"
        no-gutters
        align="center"
        justify="center"
        :class="['drop-zone', dragClass]"
        data-cy="drop-file-area"
        @dragover.prevent="handleDragOver"
        @dragleave.prevent="handleDragLeave"
        @drop.prevent
        @drop="handleDrop"
        @click="handleDropZoneClick"
      >
        <div>
          <sn-btn
            :text="multiple ? 'Select files': 'Select file'"
            type="secondary"
            size="standard"
            data-cy="select-file-btn"
            prepend-icon="sn-icon-upload"
          />
        </div>
        <span class="ml-4">
          {{ multiple ? 'or drop them here' : 'or drop your file here' }}
        </span>
        <input
          ref="fileInput"
          :accept="accept"
          :multiple="multiple"
          name="fileInput"
          style="display: none"
          type="file"
          data-cy="file-input"
          @change="handleInput"
        >
      </v-row>
      <div
        v-if="files.length"
        :key="renderKey"
        class="file-list"
      >
        <v-row
          v-for="file in files"
          :key="file.guid"
          no-gutters
          align="center"
          :class="isProcessing(file.guid) ? '' : 'added-file'"
        >
          <v-row
            v-if="isProcessing(file.guid)"
            no-gutters
            class="align-content-center pa-4"
          >
            <div class="sn-body-1 sn-text-primary display-name">
              {{ getDisplayName(file) }}
            </div>
            <v-progress-linear
              color="snBrand"
              background-color="snGray02"
              indeterminate
              rounded
              height="8"
              class="mt-5"
            />
          </v-row>
          <template v-else>
            <v-col cols="auto">
              <sn-avatar
                background-class="sn-background-brand-01"
                :icon="icon"
                :icon-style="iconStyle"
              />
            </v-col>
            <v-col class="ml-6">
              <div
                v-if="!file.editingName"
                class="sn-body-1 sn-text-primary display-name"
                @click="fileClick(file)"
              >
                {{ getDisplayName(file) }}
              </div>
              <sn-text-field
                v-if="file.editingName"
                :ref="`${file.guid}_text_field`"
                v-model="file.userFilename"
                @blur="fileBlur(file)"
              />
              <v-chip
                v-if="pillProperties"
                class="mt-2 sn-body-2"
                :color="pillProperties.color"
                :text-color="pillProperties.textColor"
              >
                {{ pillProperties.text }}
              </v-chip>
            </v-col>
            <v-col
              v-if="mayView"
              cols="auto"
              class="ml-4"
            >
              <sn-btn
                text="View"
                type="secondary"
                size="standard"
                @click="viewFile(file)"
              />
            </v-col>
            <v-col
              v-if="mayRemove"
              cols="auto"
              class="ml-4"
            >
              <v-icon
                color="snPrimary"
                size="24"
                type=""
                style="cursor: pointer"
                @click.stop="$emit('fileRemoved');clearFile(file.guid)"
              >
                $snExitCircle
              </v-icon>
            </v-col>
          </template>
        </v-row>
      </div>
    </div>
  </div>
</template>

<script>

import snTextField from '~/components/snui/v2/snTextField/snTextField'
import snBtn from '~/components/snui/v2/snBtn/snBtn'
import snAvatar from '~/components/snui/v2/snAvatar/snAvatar'
import { snBrand, snSuccess01, snSuccess04, snCaution01, snCaution, snError01, snError03 } from '~/assets/css/snui/_snColorVariables.scss'

export default {
  components: {
    snTextField,
    snAvatar,
    snBtn
  },
  props: {
    accept: {
      type: String,
      required: false,
      default: undefined
    },
    processing: {
      type: Boolean,
      default: false,
      required: false
    },
    processingGuids: {
      type: Array,
      default: () => [],
      required: false
    },
    preselectedFile: {
      type: [Object, File],
      default: null,
      required: false
    },
    preselectedFiles: {
      type: Array,
      default: () => [],
      required: false
    },
    mayRemove: {
      type: Boolean,
      default: false,
      required: false
    },
    mayView: {
      type: Boolean,
      default: false,
      required: false
    },
    mayEditName: {
      type: Boolean,
      default: false,
      required: false
    },
    height: {
      type: Number,
      default: 100,
      required: false
    },
    uploadObj: {
      type: Object,
      default: null,
      required: false
    },
    multiple: {
      type: Boolean,
      default: false,
      required: false
    },
    chipText: {
      type: String,
      default: '',
      required: false
    },
    chipType: {
      type: String,
      default: 'error',
      required: false,
      validator (chipType) {
        return ['success', 'warning', 'error'].includes(chipType)
      }
    },
    icon: {
      type: String,
      default: '$snDocument'
    }
  },
  data () {
    return {
      files: [],
      renderKey: null,
      dragClass: ''
    }
  },
  computed: {
    displayName () {
      return this.file ? this.file.name : ''
    },
    pillProperties () {
      const colors = {
        success: {
          color: snSuccess01,
          textColor: snSuccess04
        },
        warning: {
          color: snCaution01,
          textColor: snCaution
        },
        error: {
          color: snError01,
          textColor: snError03
        }
      }

      if (this.chipText) {
        return {
          text: this.chipText,
          ...colors[this.chipType]
        }
      }

      if (!this.uploadObj) return null

      if (this.uploadObj.error === true) {
        return {
          text: 'Upload failed',
          ...colors.error
        }
      } else {
        return {
          text: 'Ready to upload',
          ...colors.success
        }
      }
    },
    boxStyles () {
      return { '--box-height': this.height + 'px' }
    },
    iconStyle () {
      return `color: ${snBrand}`
    }
  },
  watch: {
    files (val) {
      if (this.multiple) {
        this.$emit('files', val)
      } else if (val.length) {
        this.$emit('file', val[0])
      } else {
        this.$emit('file', null)
      }
    }
  },
  created () {
    if (this.preselectedFiles.length > 0 && this.multiple === false) {
      throw new Error('Must use `multiple` prop when using `preselectedFiles`')
    }

    if (this.preselectedFile) {
      this.files = [this.preselectedFile]
    } else if (this.preselectedFiles) {
      this.files = this.preselectedFiles
    }
  },
  methods: {
    handleInput (event) {
      for (const file of event.target.files) {
        file.guid = crypto.randomUUID()
        file.editingName = false
        file.userFilename = file.name
        this.files.push(file)
      }
    },
    handleDrop (event) {
      const droppedFiles = event.dataTransfer.files
      for (const file of droppedFiles) {
        if (!this.accept || this.accept.includes(file.type)) {
          this.files.push(file)
        }
      }
    },
    handleDropZoneClick () {
      this.$refs.fileInput.click()
    },
    handleDragOver () {
      this.dragClass = 'drag-class'
    },
    handleDragLeave () {
      this.dragClass = ''
    },
    // If no guid is provided, all files will be cleared.
    clearFile (guid = null) {
      !guid ? this.files = [] : this.files = this.files.filter(file => file.guid !== guid)
    },
    getDisplayName (file) {
      return file?.userFilename || file?.name || ''
    },
    isProcessing (guid) {
      return this.processing || this.processingGuids.includes(guid)
    },
    fileClick (file) {
      if (this.mayEditName) {
        file.editingName = true
        this.renderKey = crypto.randomUUID()
        this.$nextTick(() => {
          this.$refs[`${file.guid}_text_field`][0].$refs.textField.focus()
        })
      }
    },
    fileBlur (file) {
      file.editingName = !file.editingName
      this.renderKey = crypto.randomUUID()
      this.$emit('file-name-changed', file.guid, file.userFilename)
    },
    viewFile (file) {
      this.$emit('view', file)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~/assets/css/snui';
.sn-file-input {
  min-height: var(--box-height);
  display: flex;
  flex-direction: column;
  gap: 24px;

  .drop-zone {
    border-radius: 16px;
    border: 2px dashed $sn-gray-04;
    cursor: pointer;
    transition: background-color 200ms;
    min-height: var(--box-height);

    &:hover {
      background-color: $sn-brand-01;
      transition: background-color 300ms;
    }
  }
  .added-file {
    border-radius: 8px;
    border: 1px solid $sn-gray-04;
    padding: 16px 16px;
    background-color: $sn-white;
  }
}

.drag-class {
  background-color: $sn-brand-01;
}

.file-list {
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 24px;
}

.display-name {
  overflow: hidden;
  text-overflow: ellipsis;
  overflow-wrap: anywhere;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
</style>
